import React, { useId } from 'react';
import { useMotorSound } from './useMotorSound';

export interface AcMotor3PhaseProps {
  isRunning?: boolean;
  isOverload?: boolean;
  speed?: number;
  direction?: 'cw' | 'ccw';
  color?: string; // Stator body base color (default: Siemens industrial green '#146E46')
  width?: string | number;
  height?: string | number;
  label?: string | undefined;
  interactive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<SVGSVGElement>) => void;
  onPointerDown?: (e: React.PointerEvent<SVGSVGElement>) => void;
}

function parseColorToRgb(colorStr: string): { r: number; g: number; b: number } {
  const hexMatch = colorStr.match(/#([0-9a-fA-F]{3,8})/);
  if (hexMatch && hexMatch[1]) {
    let hex: string = hexMatch[1];
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    const num = parseInt(hex.slice(0, 6), 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255,
    };
  }

  const lower = colorStr.toLowerCase();
  if (lower.includes('primary') || lower.includes('blue')) return { r: 59, g: 130, b: 246 };
  if (lower.includes('danger') || lower.includes('red')) return { r: 239, g: 68, b: 68 };
  if (lower.includes('warning') || lower.includes('yellow')) return { r: 234, g: 179, b: 8 };
  if (lower.includes('success')) return { r: 34, g: 170, b: 34 };
  if (lower.includes('cyan')) return { r: 8, g: 145, b: 178 };
  if (lower.includes('orange')) return { r: 234, g: 88, b: 12 };
  if (lower.includes('gray') || lower.includes('grey')) return { r: 75, g: 85, b: 99 };
  if (lower.includes('white')) return { r: 255, g: 255, b: 255 };

  return { r: 20, g: 110, b: 70 };
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  l = Math.max(0, Math.min(1, l));
  s = Math.max(0, Math.min(1, s));
  h = ((h % 360) + 360) % 360;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;

  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function computeMotorShades(baseColorStr: string, isOverload: boolean) {
  if (isOverload) {
    return {
      bodyBaseColor: '#991b1b',
      bodyLightColor: '#ef4444',
      bodyMidColor: '#b91c1c',
      bodyDarkColor: '#7f1d1d',
    };
  }

  const rgb = parseColorToRgb(baseColorStr);

  // Exact default Siemens green preset
  if (rgb.r === 20 && rgb.g === 110 && rgb.b === 70) {
    return {
      bodyBaseColor: '#146E46',
      bodyLightColor: '#22B573',
      bodyMidColor: '#1A8C59',
      bodyDarkColor: '#0e4a2f',
    };
  }

  const [h, s, l] = rgbToHsl(rgb.r, rgb.g, rgb.b);

  if (l > 0.9) {
    return {
      bodyBaseColor: '#f1f5f9',
      bodyLightColor: '#ffffff',
      bodyMidColor: '#e2e8f0',
      bodyDarkColor: '#94a3b8',
    };
  }

  if (l < 0.15) {
    return {
      bodyBaseColor: '#1f2937',
      bodyLightColor: '#4b5563',
      bodyMidColor: '#374151',
      bodyDarkColor: '#111827',
    };
  }

  const lightL = Math.min(0.88, l + (1 - l) * 0.40);
  const midL = Math.max(0.12, l * 0.82);
  const darkL = Math.max(0.08, l * 0.55);

  return {
    bodyBaseColor: hslToHex(h, s, l),
    bodyLightColor: hslToHex(h, Math.min(1, s * 1.05), lightL),
    bodyMidColor: hslToHex(h, s, midL),
    bodyDarkColor: hslToHex(h, Math.min(1, s * 1.1), darkL),
  };
}

export const AcMotor3PhaseSvg: React.FC<AcMotor3PhaseProps> = ({
  isRunning = false,
  isOverload = false,
  speed = 100,
  direction = 'cw',
  color = '#146E46',
  width = '100%',
  height = '100%',
  label,
  interactive = true,
  className = '',
  style,
  onClick,
  onPointerDown,
}) => {
  const uid = useId().replace(/:/g, '');
  const { isSpinning, isStopping } = useMotorSound(isRunning);
  const isRotating = isRunning || isSpinning;

  // Speed calculation (spin animation duration)
  const normalizedSpeed = Math.max(10, Math.min(100, speed));
  const baseSpinDuration = 0.15 + (100 - normalizedSpeed) * 0.008;
  const spinDuration = (isStopping ? baseSpinDuration * 2.2 : baseSpinDuration).toFixed(2);

  // Dynamic color shades (guarantees entire motor frame, fins, ribs, feet, and cowl match the selected color)
  const { bodyBaseColor, bodyLightColor, bodyMidColor, bodyDarkColor } = computeMotorShades(color, isOverload);

  const ledColor = isOverload ? '#ef4444' : isRunning ? '#22c55e' : (isStopping ? '#eab308' : '#4b5563');

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 451.393 284.124"
      width={width}
      height={height}
      className={`interactive-3phase-motor ${className}`}
      style={{
        display: 'block',
        overflow: 'visible',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'filter 0.2s ease',
        ...style,
      }}
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      <defs>
        {/* Stator Body Gradient */}
        <linearGradient id={`statorGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={bodyLightColor} />
          <stop offset="22%" stopColor={bodyLightColor} />
          <stop offset="42%" stopColor={bodyBaseColor} />
          <stop offset="70%" stopColor={bodyMidColor} />
          <stop offset="100%" stopColor={bodyDarkColor} />
        </linearGradient>

        {/* Stator Cylindrical 3D Specular Highlight Sheen */}
        <linearGradient id={`statorSheenGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.32" />
          <stop offset="40%" stopColor="#ffffff" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        {/* Soft Ground Contact Shadow Gradient */}
        <radialGradient id={`groundShadowGrad-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0.5" />
          <stop offset="60%" stopColor="#000000" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>

        {/* Shadow Cast by Top Box onto Stator Curve */}
        <linearGradient id={`topBoxShadowGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </linearGradient>

        {/* Front End-Shield Cast Flange Gradient */}
        <linearGradient id={`frontFlangeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={bodyDarkColor} />
          <stop offset="35%" stopColor={bodyBaseColor} />
          <stop offset="70%" stopColor={bodyLightColor} />
          <stop offset="100%" stopColor={bodyDarkColor} />
        </linearGradient>

        {/* Shaft Metallic Steel Cylindrical Gradient */}
        <linearGradient id={`shaftGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#374151" />
          <stop offset="12%" stopColor="#64748b" />
          <stop offset="28%" stopColor="#cbd5e1" />
          <stop offset="42%" stopColor="#f8fafc" />
          <stop offset="58%" stopColor="#cbd5e1" />
          <stop offset="80%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Lathe-Turned Tip Radial/Linear Gradient */}
        <linearGradient id={`shaftTipGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="45%" stopColor="#cbd5e1" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>

        {/* Machined Bearing Collar Gradient */}
        <linearGradient id={`collarGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="25%" stopColor="#94a3b8" />
          <stop offset="45%" stopColor="#f1f5f9" />
          <stop offset="75%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Terminal Box Cast Slate / Graphite Gradient (Distinct Industrial Color) */}
        <linearGradient id={`terminalBoxGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="40%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Terminal Box Cover Inset Gradient */}
        <linearGradient id={`terminalCoverGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="50%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#090d16" />
        </linearGradient>

        {/* Terminal Lid Gradient */}
        <linearGradient id={`terminalLidGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="25%" stopColor="#334155" />
          <stop offset="75%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Rotating Keyway/Stripe Pattern */}
        <linearGradient id={`shaftSpinGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#374151" />
          <stop offset="25%" stopColor="#e5e7eb" />
          <stop offset="50%" stopColor="#4b5563" />
          <stop offset="75%" stopColor="#d1d5db" />
          <stop offset="100%" stopColor="#1f2937" />
        </linearGradient>

        {/* LED Glow Filter */}
        <filter id={`ledGlow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <style>{`
        @keyframes motor-vibration-${uid} {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-0.35px, 0.35px); }
          50% { transform: translate(0.35px, -0.35px); }
          75% { transform: translate(-0.35px, -0.35px); }
        }
        @keyframes shaft-spin-${uid} {
          0% { transform: scaleY(1); opacity: 0.95; }
          50% { transform: scaleY(0.78); opacity: 0.75; }
          100% { transform: scaleY(1); opacity: 0.95; }
        }
        @keyframes led-pulse-${uid} {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes airflow-pulse-${uid} {
          0% { opacity: 0.2; transform: translateX(0); }
          50% { opacity: 0.8; transform: translateX(4px); }
          100% { opacity: 0.2; transform: translateX(0); }
        }
        .motor-vibrating-${uid} {
          animation: motor-vibration-${uid} 0.06s linear infinite;
        }
        .shaft-spinning-${uid} {
          transform-origin: 40px 150px;
          animation: shaft-spin-${uid} ${spinDuration}s linear infinite;
          animation-direction: ${direction === 'ccw' ? 'reverse' : 'normal'};
        }
        .overload-pulsing-${uid} {
          animation: led-pulse-${uid} 0.6s ease-in-out infinite;
        }
        .airflow-running-${uid} {
          animation: airflow-pulse-${uid} 0.5s ease-in-out infinite;
        }
      `}</style>

      {/* Main Motor Assembly with micro-vibration when running */}
      <g className={isRotating ? `motor-vibrating-${uid}` : ''}>
        
        {/* Soft Ground Contact Shadow (Anchors motor realistically to the floor) */}
        <g id="ground-shadow" opacity="0.45" pointerEvents="none">
          <ellipse cx="230" cy="281" rx="165" ry="3.8" fill={`url(#groundShadowGrad-${uid})`} />
          <ellipse cx="144" cy="281.5" rx="34" ry="2" fill="#000000" opacity="0.6" />
          <ellipse cx="291" cy="281.5" rx="34" ry="2" fill="#000000" opacity="0.6" />
          <ellipse cx="40" cy="280.5" rx="40" ry="1.6" fill="#000000" opacity="0.2" />
        </g>

        {/* Main Stator Frame Housing (Geometry from ac_motor_3faz..svg) */}
        <g id="motor-frame">
          <path
            fill={`url(#statorGrad-${uid})`}
            stroke="rgba(0, 0, 0, 0.35)"
            strokeWidth="1.2"
            d="M425.253,51.339h-86.675v-2.077c0-1.99-3.08-3.603-6.879-3.603c-3.799,0-6.879,1.613-6.879,3.603v2.077
              h-6.879v9.631h-2.131v-0.682v-4.129H178.986V52.99h12.382V22.723h2.376V4.837h-2.376h-1.993c0.263-0.137,0.41-0.287,0.41-0.445
              V2.774c0-1.532-2.371-2.774-5.295-2.774c-2.925,0-5.295,1.242-5.295,2.774v1.618c0,0.158,0.147,0.308,0.41,0.445h-72.985
              c0.263-0.137,0.41-0.287,0.41-0.445V2.774c0-1.532-2.371-2.774-5.295-2.774c-2.925,0-5.295,1.242-5.295,2.774v1.618
              c0,0.158,0.147,0.308,0.41,0.445h-1.786h-2.376v17.885h2.376V52.99h12.382v7.98h-16.51v46.777h-9.006c-1.105,0-2,0.895-2,2v26.892
              H2c-1.105,0-2,0.895-2,2v23.516c0,1.105,0.895,2,2,2h77.928v26.892c0,1.105,0.895,2,2,2h9.006v46.777h35.771v28.412h-9.508v15.889
              h54.476l0.556-2.752h90.803l0.556,2.752h54.476v-15.889h-8.377v-28.412h8.255v9.631h7.331c-0.287,0.165-0.452,0.344-0.452,0.531
              v2.102c0,1.99,3.08,3.603,6.879,3.603c3.799,0,6.879-1.613,6.879-3.603v-2.102c0-0.187-0.164-0.366-0.452-0.531h87.127
              c14.437,0,26.14-44.349,26.14-99.057S439.69,51.339,425.253,51.339z"
          />

          {/* Stator Cylindrical 3D Specular Highlight Sheen running along the upper curve */}
          <rect x="107" y="58" width="228" height="16" fill={`url(#statorSheenGrad-${uid})`} rx="3" pointerEvents="none" />

          {/* Soft Shadow Cast by Top Box onto Stator Cylinder */}
          <rect x="105" y="53" width="85" height="7" fill={`url(#topBoxShadowGrad-${uid})`} pointerEvents="none" />

          {/* Front Bearing End-Shield (Ön Rulman Kapağı / Flanş) */}
          <g id="front-end-shield">
            <ellipse cx="85" cy="150.397" rx="6" ry="32" fill={`url(#frontFlangeGrad-${uid})`} stroke="rgba(0,0,0,0.35)" strokeWidth="0.8" />
            {/* Front Flange Fastener Bolts */}
            {[
              { bx: 93, by: 122 },
              { bx: 93, by: 150.4 },
              { bx: 93, by: 178 },
            ].map((bolt, idx) => (
              <g key={`flange-bolt-${idx}`}>
                <circle cx={bolt.bx} cy={bolt.by} r="2.2" fill="#94a3b8" stroke="#334155" strokeWidth="0.5" />
                <polygon points={`${bolt.bx},${bolt.by - 1.2} ${bolt.bx + 1},${bolt.by - 0.6} ${bolt.bx + 1},${bolt.by + 0.6} ${bolt.bx},${bolt.by + 1.2} ${bolt.bx - 1},${bolt.by + 0.6} ${bolt.bx - 1},${bolt.by - 0.6}`} fill="#1e293b" />
              </g>
            ))}
            {/* Front Shaft Oil Seal Ring */}
            <rect x="79" y="132" width="3.5" height="36.8" rx="1.5" fill="#18181b" stroke="#334155" strokeWidth="0.6" />
          </g>

          {/* Stator Cooling Fins with 3D Depth (Top Highlights & Under-Shadows) */}
          <g id="stator-fins" opacity="0.95">
            {[
              "107.444,66.291 114.323,56.159 315.81,56.159 315.81,60.288 315.81,71.293 107.444,71.293",
              "107.444,75.632 114.323,65.5 315.81,65.5 315.81,69.629 315.81,80.634 107.444,80.634",
              "107.444,88.451 114.323,80.818 315.81,80.818 315.81,84.947 315.81,93.452 107.444,93.452",
              "107.444,100.833 114.323,95.701 315.81,95.701 315.81,99.829 315.81,105.834 107.444,105.834",
              "315.81,192.949 114.323,192.949 107.444,187.817 107.444,192.818 114.323,197.951 315.81,197.951",
              "315.81,208.581 114.323,208.581 107.444,200.949 107.444,205.951 114.323,213.583 315.81,213.583",
              "315.81,224.218 114.323,224.218 107.444,214.086 107.444,219.087 114.323,229.22 315.81,229.22",
            ].map((pts, idx) => (
              <g key={`fin-${idx}`}>
                <polygon fill={bodyMidColor} points={pts} />
                {/* 3D Top Edge Specular Reflection */}
                <polyline points={pts.split(' ').slice(0, 3).join(' ')} stroke="rgba(255,255,255,0.3)" strokeWidth="0.9" fill="none" />
                {/* 3D Bottom Groove Cast Shadow */}
                <polyline points={pts.split(' ').slice(3, 6).join(' ')} stroke="rgba(0,0,0,0.38)" strokeWidth="1.2" fill="none" />
              </g>
            ))}

            {/* Horizontal Stator Rib Strips with 3D Bevel Relief */}
            {[112.558, 125.09, 137.623, 150.155, 162.688, 175.22].map((ry, idx) => (
              <g key={`rib-${idx}`}>
                {/* Rib Top Specular Catch */}
                <rect x="183.734" y={ry} fill="#ffffff" width="132.076" height="1.4" opacity="0.35" />
                {/* Rib Core Body */}
                <rect x="183.734" y={ry + 1.2} fill={bodyLightColor} width="132.076" height="2.6" opacity="0.9" />
                {/* Rib Under-Shadow Groove */}
                <rect x="183.734" y={ry + 3.8} fill={bodyDarkColor} width="132.076" height="1.4" opacity="0.75" />
              </g>
            ))}
          </g>

          {/* Mounting Feet (Ayaklar) with 3D Cast Flanges & Foundation Bolts */}
          <g id="motor-feet">
            <polygon
              fill={bodyDarkColor}
              stroke="rgba(0,0,0,0.4)"
              strokeWidth="0.9"
              points="272.096,268.236 261.411,224.579 176.111,224.579 165.426,268.236 117.197,268.236 117.197,284.124 171.674,284.124 172.229,281.372 263.032,281.372 263.587,284.124 318.064,284.124 318.064,268.236"
            />
            {/* Feet Top Chamfer Highlights */}
            <line x1="165.4" y1="268.2" x2="117.2" y2="268.2" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
            <line x1="318.1" y1="268.2" x2="272.1" y2="268.2" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />

            {/* Machined Steel Foundation Mounting Pads */}
            <rect x="122" y="271.5" width="44" height="7" rx="1.5" fill="#334155" stroke="#1e293b" strokeWidth="0.7" />
            <line x1="123" y1="272" x2="165" y2="272" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />

            <rect x="269" y="271.5" width="44" height="7" rx="1.5" fill="#334155" stroke="#1e293b" strokeWidth="0.7" />
            <line x1="270" y1="272" x2="312" y2="272" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />

            {/* Foundation Anchor Bolts & Washers */}
            <g transform="translate(144, 275)">
              <rect x="-4" y="-3.5" width="8" height="7" rx="1.5" fill="#0f172a" />
              <circle cx="0" cy="0" r="2.8" fill="#94a3b8" stroke="#334155" strokeWidth="0.5" />
              <polygon points="0,-1.4 1.2,-0.7 1.2,0.7 0,1.4 -1.2,0.7 -1.2,-0.7" fill="#1e293b" />
            </g>

            <g transform="translate(291, 275)">
              <rect x="-4" y="-3.5" width="8" height="7" rx="1.5" fill="#0f172a" />
              <circle cx="0" cy="0" r="2.8" fill="#94a3b8" stroke="#334155" strokeWidth="0.5" />
              <polygon points="0,-1.4 1.2,-0.7 1.2,0.7 0,1.4 -1.2,0.7 -1.2,-0.7" fill="#1e293b" />
            </g>
          </g>
        </g>

        {/* Top Terminal Box Housing (Üst Klemens Kutusu: Distinct Industrial Graphite) */}
        <g id="terminal-top-box">
          {/* Housing Body */}
          <rect
            x="105"
            y="12"
            width="84"
            height="41"
            rx="2"
            fill={`url(#terminalBoxGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="1.2"
          />

          {/* Stiffening Cooling Ribs on Top Box */}
          <rect x="125" y="16" width="3.5" height="33" rx="1" fill="#475569" opacity="0.55" />
          <rect x="146.5" y="16" width="3.5" height="33" rx="1" fill="#475569" opacity="0.55" />
          <rect x="168" y="16" width="3.5" height="33" rx="1" fill="#475569" opacity="0.55" />

          {/* Top Box Flanged Lid (Klemens Kapağı) */}
          <rect
            x="103"
            y="4.8"
            width="88"
            height="7.5"
            rx="1.8"
            fill={`url(#terminalLidGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="1"
          />
          {/* Top Lid Specular Highlight */}
          <line x1="104" y1="5.4" x2="190" y2="5.4" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" />
          {/* Lid Overhang Under-Shadow */}
          <line x1="104" y1="12.3" x2="190" y2="12.3" stroke="#000000" strokeWidth="1.4" opacity="0.75" />

          {/* Stainless Lid Fastener Screws */}
          {[112, 147, 182].map((bx, idx) => (
            <g key={`top-bolt-${idx}`}>
              <circle cx={bx} cy="8.5" r="2.2" fill="#94a3b8" stroke="#334155" strokeWidth="0.5" />
              <circle cx={bx} cy="8.5" r="1.1" fill="#1e293b" />
            </g>
          ))}

          {/* High Voltage Caution Triangle on Top Box */}
          <polygon points="147,25 141.5,34.5 152.5,34.5" fill="#f59e0b" stroke="#b45309" strokeWidth="0.6" />
          <path d="M147.2,27.2 L144.8,30.6 L147.6,30.6 L146.4,33.8 L149.2,29.8 L146.8,29.8 Z" fill="#0f172a" />

          {/* Cable Gland Entry (Kablo Rakoru) on Right Side */}
          <rect x="189" y="24" width="5.5" height="15" rx="1.2" fill="#475569" stroke="#1e293b" strokeWidth="0.8" />
          <line x1="190.8" y1="26" x2="190.8" y2="37" stroke="#cbd5e1" strokeWidth="0.7" />
          <line x1="192.6" y1="26" x2="192.6" y2="37" stroke="#cbd5e1" strokeWidth="0.7" />
        </g>

        {/* Side Terminal Inspection & Indicator Plate (Klemens Tablosu: Distinct Industrial Graphite) */}
        <g id="terminal-box">
          {/* Subtle Outer Plate Drop Shadow */}
          <rect x="113" y="115.5" width="67" height="67" rx="3.5" fill="#000000" opacity="0.32" />

          {/* Outer Cast Mounting Bevel */}
          <path
            fill={`url(#terminalBoxGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="1.4"
            d="M178.361,180.939h-64.79c-1.105,0-2-0.895-2-2v-64.79c0-1.105,0.895-2,2-2h64.79c1.105,0,2,0.895,2,2v64.79C180.361,180.044,179.466,180.939,178.361,180.939z"
          />

          {/* 3D Bevel Edges (Top-Left Light, Bottom-Right Shadow) */}
          <path d="M 112 180 L 112 114.5 L 179.5 114.5" stroke="rgba(255,255,255,0.3)" strokeWidth="1.1" fill="none" />
          <path d="M 179.5 114.5 L 179.5 180 L 112 180" stroke="rgba(0,0,0,0.6)" strokeWidth="1.3" fill="none" />

          {/* Inner Highlight Edge */}
          <rect
            x="113.2"
            y="115.8"
            width="64.8"
            height="64.2"
            rx="2"
            fill="none"
            stroke="#475569"
            strokeWidth="0.8"
            opacity="0.65"
          />

          {/* Terminal Plate Inset Area with Inner Shadow */}
          <rect
            x="116.5"
            y="118.8"
            width="58"
            height="58"
            rx="2.5"
            fill={`url(#terminalCoverGrad-${uid})`}
            stroke="#090d16"
            strokeWidth="1"
          />
          <line x1="117.5" y1="119.8" x2="173.5" y2="119.8" stroke="rgba(0,0,0,0.65)" strokeWidth="0.8" />

          {/* 4 Corner Stainless Steel Hex Socket Fasteners */}
          {[
            { x: 120.732, y: 122.686 },
            { x: 171.482, y: 122.686 },
            { x: 120.732, y: 173.06 },
            { x: 171.482, y: 173.06 },
          ].map((pos, idx) => (
            <g key={`fastener-${idx}`} transform={`translate(${pos.x}, ${pos.y})`}>
              <circle cx="0" cy="0" r="3.2" fill="#94a3b8" stroke="#334155" strokeWidth="0.6" />
              <polygon points="0,-1.5 1.3,-0.75 1.3,0.75 0,1.5 -1.3,0.75 -1.3,-0.75" fill="#1e293b" />
            </g>
          ))}

          {/* Terminal Header Plate Text */}
          <text
            x="145.5"
            y="128.5"
            fill="#94a3b8"
            fontSize="4.8"
            fontWeight="bold"
            textAnchor="middle"
            letterSpacing="0.6"
          >
            3~ MOT • IP55
          </text>

          {/* Brass Terminal Studs, Copper Bridges & 3-Phase Indicator LEDs (L1, L2, L3) */}
          <g transform="translate(129.5, 134)">
            {/* Copper Busbar Links between terminals */}
            <line x1="8" y1="12" x2="16" y2="12" stroke="#d97706" strokeWidth="1.4" strokeLinecap="round" opacity="0.85" />
            <line x1="16" y1="12" x2="24" y2="12" stroke="#d97706" strokeWidth="1.4" strokeLinecap="round" opacity="0.85" />

            {/* L1 Terminal & Indicator */}
            <g>
              <polygon points="8,8.8 10.8,10.4 10.8,13.6 8,15.2 5.2,13.6 5.2,10.4" fill="#d97706" stroke="#92400e" strokeWidth="0.4" />
              <circle cx="8" cy="12" r="3.2" fill="#0f172a" stroke="#475569" strokeWidth="0.6" />
              <circle
                cx="8"
                cy="12"
                r="2.2"
                fill={ledColor}
                filter={isRunning || isOverload ? `url(#ledGlow-${uid})` : undefined}
              />
              <text x="8" y="21.5" fill="#cbd5e1" fontSize="4.2" fontWeight="bold" textAnchor="middle">L1</text>
            </g>

            {/* L2 Terminal & Indicator */}
            <g>
              <polygon points="16,8.8 18.8,10.4 18.8,13.6 16,15.2 13.2,13.6 13.2,10.4" fill="#d97706" stroke="#92400e" strokeWidth="0.4" />
              <circle cx="16" cy="12" r="3.2" fill="#0f172a" stroke="#475569" strokeWidth="0.6" />
              <circle
                cx="16"
                cy="12"
                r="2.2"
                fill={ledColor}
                filter={isRunning || isOverload ? `url(#ledGlow-${uid})` : undefined}
              />
              <text x="16" y="21.5" fill="#cbd5e1" fontSize="4.2" fontWeight="bold" textAnchor="middle">L2</text>
            </g>

            {/* L3 Terminal & Indicator */}
            <g>
              <polygon points="24,8.8 26.8,10.4 26.8,13.6 24,15.2 21.2,13.6 21.2,10.4" fill="#d97706" stroke="#92400e" strokeWidth="0.4" />
              <circle cx="24" cy="12" r="3.2" fill="#0f172a" stroke="#475569" strokeWidth="0.6" />
              <circle
                cx="24"
                cy="12"
                r="2.2"
                fill={ledColor}
                filter={isRunning || isOverload ? `url(#ledGlow-${uid})` : undefined}
              />
              <text x="24" y="21.5" fill="#cbd5e1" fontSize="4.2" fontWeight="bold" textAnchor="middle">L3</text>
            </g>

            {/* Voltage Badge */}
            <rect x="1" y="27" width="30" height="9.5" rx="1.8" fill="#090d16" stroke="#334155" strokeWidth="0.6" />
            <text x="5" y="34.2" fill="#f59e0b" fontSize="5.5" fontWeight="bold">⚡</text>
            <text x="18.5" y="34" fill="#e2e8f0" fontSize="4.6" fontWeight="bold" textAnchor="middle" letterSpacing="0.4">
              3~ 400V
            </text>
          </g>
        </g>

        {/* Output Shaft (Rendered in front of motor-frame: Metallic Steel Finish with 3D Depth) */}
        <g id="motor-shaft" className={isRotating ? `shaft-spinning-${uid}` : ''}>
          {/* Soft Shadow Under Shaft */}
          <ellipse cx="40" cy="166" rx="36" ry="1.8" fill="#000000" opacity="0.22" pointerEvents="none" />

          {/* Main Machined Steel Cylindrical Shaft */}
          <rect
            x="0"
            y="136.639"
            width="82.43"
            height="27.516"
            rx="1.8"
            fill={`url(#shaftGrad-${uid})`}
            stroke="#1e293b"
            strokeWidth="0.9"
          />

          {/* Cylindrical Specular Glint Streak */}
          <line x1="4" y1="140" x2="76" y2="140" stroke="#ffffff" strokeWidth="1.2" strokeLinecap="round" opacity="0.55" />

          {/* Machined Bearing Collar / Step Shoulder with 3D Relief */}
          <rect
            x="78.5"
            y="133.5"
            width="4.5"
            height="33.8"
            rx="1"
            fill={`url(#collarGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.8"
          />
          <line x1="79.5" y1="134" x2="79.5" y2="167" stroke="#ffffff" strokeWidth="0.7" opacity="0.65" />
          <line x1="82.5" y1="134" x2="82.5" y2="167" stroke="#000000" strokeWidth="0.8" opacity="0.5" />

          {/* Shaft Chamfer Face & Lathe Center Drilling Hole (Punta Yuvası) */}
          <ellipse cx="2" cy="150.397" rx="2" ry="13.7" fill={`url(#shaftTipGrad-${uid})`} stroke="#334155" strokeWidth="0.8" />
          <ellipse cx="2" cy="150.397" rx="0.8" ry="2.6" fill="#0f172a" />

          {/* Keyway Slot (Kama Kanalı - DIN 6885) */}
          <rect
            x="13"
            y="147.2"
            width="53"
            height="6.4"
            rx="2"
            fill="#090d16"
            stroke="#1e293b"
            strokeWidth="0.7"
            opacity={isRotating ? '0.75' : '0.95'}
          />
          {/* Keyway Upper Highlight */}
          <line x1="15" y1="147.5" x2="64" y2="147.5" stroke="#ffffff" strokeWidth="0.75" opacity="0.7" />

          {/* Feather Key (Kama) inside Slot */}
          <rect
            x="15"
            y="148"
            width="48"
            height="4.8"
            rx="1.5"
            fill={`url(#shaftTipGrad-${uid})`}
            stroke="#334155"
            strokeWidth="0.5"
            opacity={isRotating ? '0.6' : '0.9'}
          />
          <line x1="17" y1="148.8" x2="61" y2="148.8" stroke="#ffffff" strokeWidth="0.6" opacity="0.85" />

          {/* Dynamic Rotation Reflection Effect */}
          {isRotating && (
            <rect x="2" y="138" width="76" height="7" fill="rgba(255, 255, 255, 0.42)" rx="2" />
          )}
        </g>

        {/* Eyebolts (Kaldırma Halkaları / Mapalar: Forged Steel 3D Finish) */}
        <g id="motor-eyebolts">
          {/* Eyebolt 1 */}
          <circle cx="101.7" cy="4.4" r="5.2" fill="none" stroke="#1e293b" strokeWidth="3" />
          <circle cx="101.7" cy="4.4" r="5" fill="none" stroke="#94a3b8" strokeWidth="2.2" />
          <circle cx="101.7" cy="3.8" r="4.2" fill="none" stroke="#f1f5f9" strokeWidth="0.7" opacity="0.7" />
          <circle cx="101.7" cy="4.4" r="2.2" fill="#090d16" />

          {/* Eyebolt 2 */}
          <circle cx="184.5" cy="4.4" r="5.2" fill="none" stroke="#1e293b" strokeWidth="3" />
          <circle cx="184.5" cy="4.4" r="5" fill="none" stroke="#94a3b8" strokeWidth="2.2" />
          <circle cx="184.5" cy="3.8" r="4.2" fill="none" stroke="#f1f5f9" strokeWidth="0.7" opacity="0.7" />
          <circle cx="184.5" cy="4.4" r="2.2" fill="#090d16" />
        </g>

        {/* Rear Cooling Fan Cowl & Airflow Indicator (Right Side: 3D Cowl with Intake Louvers) */}
        <g id="rear-cooling-fan">
          {/* Main Cowl Dome Rim */}
          <ellipse cx="425.253" cy="150.397" rx="14" ry="72" fill={bodyDarkColor} stroke="#0f172a" strokeWidth="1.2" />
          
          {/* Cowl Bevel Highlights & Shadows */}
          <path
            d="M 425 78.4 C 435 95 439 125 439 150.4"
            fill="none"
            stroke="rgba(255, 255, 255, 0.28)"
            strokeWidth="1.2"
          />
          <path
            d="M 439 150.4 C 439 175 435 205 425 222.4"
            fill="none"
            stroke="rgba(0, 0, 0, 0.45)"
            strokeWidth="1.4"
          />

          {/* Recessed Intake Chamber (Hava Emiş Haznesi) */}
          <ellipse cx="427" cy="150.397" rx="9" ry="52" fill="#090d16" stroke="#000" strokeWidth="1" />
          
          {/* Rotating Fan Impeller Shadow when Running */}
          <g opacity={isRotating ? 0.45 : 0.2}>
            <line x1="427" y1="110" x2="427" y2="190" stroke="#cbd5e1" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="422" y1="125" x2="432" y2="175" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="432" y1="125" x2="422" y2="175" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" />
          </g>

          {/* Protective Concentric Grille Rings (Koruma Kafes Izgaraları) */}
          <ellipse cx="427" cy="150.397" rx="6.5" ry="38" fill="none" stroke="#1e293b" strokeWidth="1.2" />
          <ellipse cx="427" cy="150.397" rx="4" ry="24" fill="none" stroke="#334155" strokeWidth="1" />
          {/* Center Fan Hub */}
          <ellipse cx="427" cy="150.397" rx="2.2" ry="12" fill="#475569" stroke="#1e293b" strokeWidth="0.6" />
          
          {/* Airflow discharge trails when running */}
          {isRotating && (
            <g className={`airflow-running-${uid}`} opacity="0.8">
              <path d="M435,130 C445,125 450,135 448,140" fill="none" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M437,150 C448,150 452,158 449,165" fill="none" stroke="#7dd3fc" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M435,170 C445,175 450,165 448,160" fill="none" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" />
            </g>
          )}
        </g>

        {/* Status Badge / Label */}
        {label && (
          <g transform="translate(225, 205)" pointerEvents="none">
            <rect x="-35" y="-7" width="70" height="14" rx="4" fill="#000" opacity="0.8" stroke="#374151" strokeWidth="0.8" />
            <text x="0" y="3" fill="#ffffff" fontSize="9" fontWeight="bold" textAnchor="middle" letterSpacing="0.5">
              {label}
            </text>
          </g>
        )}
      </g>
    </svg>
  );
};
